import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { workflowService } from '../services/workflowService';

interface StepCommentsDialogProps {
  open: boolean;
  onClose: () => void;
  stepId: string;
  stepTitle: string;
  currentComments?: string;
  onCommentsUpdated?: (comments: string) => void;
}

export const StepCommentsDialog: React.FC<StepCommentsDialogProps> = ({
  open,
  onClose,
  stepId,
  stepTitle,
  currentComments = '',
  onCommentsUpdated,
}) => {
  const [comments, setComments] = useState(currentComments);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setComments(currentComments);
    setIsEditing(false);
    setError(null);
  }, [open, currentComments]);

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setComments(currentComments);
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!comments.trim()) {
      setError('Comments cannot be empty');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await workflowService.updateStepComments(stepId, comments.trim());
      setIsEditing(false);
      onCommentsUpdated?.(comments.trim());
    } catch (err) {
      setError('Failed to update comments. Please try again.');
      console.error('Error updating step comments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isEditing) {
      handleCancel();
    }
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box>
          <Typography variant="h6" component="div">
            Step Comments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stepTitle}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          {isEditing ? (
            <TextField
              fullWidth
              multiline
              rows={6}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Enter your comments about this step..."
              variant="outlined"
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          ) : (
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'grey.50',
                minHeight: 120,
                display: 'flex',
                alignItems: comments ? 'flex-start' : 'center',
                justifyContent: comments ? 'flex-start' : 'center',
              }}
            >
              {comments ? (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {comments}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No comments added yet
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        {isEditing ? (
          <>
            <Button
              onClick={handleCancel}
              startIcon={<CancelIcon />}
              disabled={isLoading}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={16} /> : <SaveIcon />}
              disabled={isLoading || !comments.trim()}
              sx={{ textTransform: 'none' }}
            >
              {isLoading ? 'Saving...' : 'Save Comments'}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleEdit}
            variant="contained"
            startIcon={<EditIcon />}
            sx={{ textTransform: 'none' }}
          >
            {comments ? 'Edit Comments' : 'Add Comments'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
